// src/components/users/UsersTable.jsx
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useGetUsers } from '../../hooks/Users/useGetUsers';
import { useDeleteUsers } from '../../hooks/Users/useDeleteUsers';
import { useDeleteAllUsers } from '../../hooks/Users/useDeleteAllUsers';
import useTableActions from '../../hooks/common/useTableActions';
import ModelShowUser from './ModelShowUser';
import TableLayout from '../global/Table/TableLayout';
import usersColumns from './Table/UsersTableColumns';
import UsersTableRow from './Table/UsersTableRow';

const UsersTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';

    const navigate = useNavigate();

    // CRUD Hooks
    const { deleteUsers, isDelete } = useDeleteUsers();
    const { deleteAllUsers: deleteAllUsersApi, isDelete: isDeletingAll } = useDeleteAllUsers();

    // ✅ استخدام Custom Hook للإجراءات المشتركة
    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        selectedItem, // ✅ إضافة selectedItem
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: '/users',
        deleteSingle: deleteUsers,
        deleteAll: deleteAllUsersApi,
        isArchive: false,
    });

    // ✨ Local State (specific to this table - for view functionality)
    const [openView, setOpenView] = React.useState(false);
    const [viewUserId, setViewUserId] = React.useState(null);

    const filters = useMemo(
        () => ({
            search: search || undefined,
        }),
        [search]
    );

    const { isLoading, error, Users, meta } = useGetUsers(page, perPage, filters);

    const sortedUsers = useMemo(() => {
        if (!Users) return [];
        const list = [...Users];
        if (order === 'asc') return list;
        return list.reverse();
    }, [Users, order]);

    // ✨ Handle View - يستخدم selectedItem من الـ Hook
    const handleView = () => {
        if (selectedItem) {
            setViewUserId(selectedItem.id);
            setOpenView(true);
        }
        handleMenuClose();
    };

    const renderRow = (params) => (
        <UsersTableRow
            key={params.key || params.item.id}
            user={params.item}
            index={params.index}
            isArabic={params.isArabic}
            isDark={params.isDark}
            page={params.page}
            perPage={params.perPage}
            order={params.order}
            totalItems={sortedUsers.length}
            selectedIds={params.selectedIds}
            onRowCheckboxClick={params.onRowCheckboxClick}
            onMenuOpen={params.onMenuOpen}
        />
    );

    return (
        <>
            <TableLayout
                title={isArabic ? 'المستخدمون' : 'Users'}
                subtitle={
                    isArabic
                        ? 'إدارة المستخدمين والصلاحيات في النظام'
                        : 'Manage system users and permissions'
                }
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={isArabic ? 'إنشاء مستخدم جديد' : 'Create new user'}
                onAddClick={() => navigate('/users/create')}
                isLoading={isLoading}
                error={error}
                items={sortedUsers}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                order={order}
                onRequestSort={handleRequestSort}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, sortedUsers)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                isBulkDeleting={isDeletingAll}
                columns={usersColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuView={handleView}
                onMenuEdit={handleEdit}
                onMenuDelete={handleMenuDelete}
                isDeletingSingle={isDelete}
                emptyLabelAr="لا توجد مستخدمين"
                emptyLabelEn="No users"
                loadErrorAr="حدث خطأ أثناء تحميل المستخدمين"
                loadErrorEn="Failed to load users"
            />

            {openView && (
                <ModelShowUser
                    UserId={viewUserId}
                    open={openView}
                    onClose={() => setOpenView(false)}
                />
            )}
        </>
    );
};

export default UsersTable;
